import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, ShieldAlert, RefreshCw, FileDown, LogIn, PlaneTakeoff, MonitorSmartphone, Lock, KeyRound, Users, Fingerprint, PackageCheck, Loader2 } from "lucide-react";

const POSTURE = {
  secure: { label: "Güvenli", cls: "bg-emerald-500/15 text-emerald-300 ring-emerald-400/30", Icon: ShieldCheck, note: "Son dönemde dikkat gerektiren güvenlik olayı yok." },
  watch: { label: "İzlemede", cls: "bg-amber-500/15 text-amber-300 ring-amber-400/30", Icon: ShieldAlert, note: "Başarısız giriş denemeleri yükseldi — izlemede tutun." },
  attention: { label: "Dikkat", cls: "bg-rose-500/15 text-rose-300 ring-rose-400/30", Icon: ShieldAlert, note: "İmkânsız seyahat / hesap kilidi / defter bütünlüğü sinyali var — inceleyin." },
};
const TONE = {
  rose: "border-l-rose-500 text-rose-600",
  amber: "border-l-amber-500 text-amber-600",
  emerald: "border-l-emerald-500 text-emerald-600",
  slate: "border-l-slate-300 text-slate-700",
};

function Sparkline({ series, color }) {
  const w = 132, h = 34, max = Math.max(1, ...series);
  const step = series.length > 1 ? w / (series.length - 1) : w;
  const pts = series.map((v, i) => `${(i * step).toFixed(1)},${(h - (v / max) * (h - 6) - 3).toFixed(1)}`).join(" ");
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="shrink-0">
      <polyline fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" points={pts} />
      {series.map((v, i) => <circle key={i} cx={i * step} cy={h - (v / max) * (h - 6) - 3} r="1.7" fill={color} />)}
    </svg>
  );
}

function TrendRow({ label, testid, series, color }) {
  const total = series.reduce((a, b) => a + b, 0);
  const half = Math.floor(series.length / 2);
  const first = series.slice(0, half).reduce((a, b) => a + b, 0);
  const last = series.slice(half).reduce((a, b) => a + b, 0);
  const dir = last > first ? "up" : last < first ? "down" : "flat";
  const arrow = { up: { t: "↑", c: "text-rose-600", n: "artışta" }, down: { t: "↓", c: "text-emerald-600", n: "düşüşte" }, flat: { t: "→", c: "text-slate-400", n: "sabit" } }[dir];
  return (
    <div data-testid={testid} className="flex items-center justify-between gap-3 rounded-xl bg-slate-50 p-3 ring-1 ring-inset ring-slate-200">
      <div className="min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
        <p className="mt-0.5 font-heading text-lg font-bold text-slate-900">{total} <span className={`text-xs font-bold ${arrow.c}`}>{arrow.t} {arrow.n}</span></p>
      </div>
      <Sparkline series={series.length ? series : [0, 0]} color={color} />
    </div>
  );
}

export default function SecurityProofDashboard() {
  const { user } = useAuth();
  const [win, setWin] = useState(24);
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/security/summary", { params: { hours: win } })
      .then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
    api.get("/security/trend", { params: { days: 7 } }).then((r) => setTrend(r.data.days || [])).catch(() => {});
  }, [win]);
  useEffect(() => { load(); }, [load]);

  const ev = data?.events || {};
  const posture = POSTURE[data?.posture] || POSTURE.secure;
  const twofaPct = data?.twofa?.total_users ? Math.round((data.twofa.enabled_users / data.twofa.total_users) * 100) : 0;

  const exportPdf = () => {
    if (!data) return;
    let company = null;
    try { company = JSON.parse(localStorage.getItem("groot_company") || "null"); } catch { company = null; }
    const co = (company && company.name) || "Groot ONE ERP";
    const genAt = new Date().toLocaleString("tr-TR");
    const winLabel = win === 24 ? "Son 24 saat" : "Son 7 gün";
    const e = data.events || {};
    const row = (k, v) => `<tr><td>${k}</td><td class="v">${v}</td></tr>`;
    const ipRows = (data.top_failing_ips || []).length
      ? data.top_failing_ips.map((ip) => `<tr><td class="mono">${ip.ip}</td><td class="v">${ip.count}</td></tr>`).join("")
      : `<tr><td colspan="2" class="muted">Başarısız giriş kaydı yok.</td></tr>`;
    const postureLabel = { secure: "GÜVENLİ", watch: "İZLEMEDE", attention: "DİKKAT" }[data.posture] || "GÜVENLİ";
    const html = `<!doctype html><html lang="tr"><head><meta charset="utf-8"><title>Güvenlik Kanıt Raporu — ${co}</title>
      <style>
        *{box-sizing:border-box} body{font-family:-apple-system,Segoe UI,Roboto,Arial,sans-serif;color:#0f172a;margin:0;padding:32px;background:#fff}
        .hd{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #0f7a3d;padding-bottom:14px;margin-bottom:18px}
        h1{font-size:20px;margin:0 0 2px} .sub{color:#64748b;font-size:12px}
        .badge{display:inline-block;padding:4px 12px;border-radius:999px;font-weight:800;font-size:12px;background:#0f172a;color:#fff}
        h2{font-size:13px;text-transform:uppercase;letter-spacing:.05em;color:#0f7a3d;margin:22px 0 6px;border-bottom:1px solid #e2e8f0;padding-bottom:4px}
        table{width:100%;border-collapse:collapse;font-size:13px} td{padding:6px 10px;border-bottom:1px solid #f1f5f9}
        td.v{text-align:right;font-weight:700} td.mono{font-family:monospace} td.muted{color:#94a3b8}
        .grid{display:grid;grid-template-columns:1fr 1fr;gap:0 32px}
        .sig{margin-top:28px;border-top:1px dashed #cbd5e1;padding-top:12px;color:#475569;font-size:12px;display:flex;justify-content:space-between;align-items:center}
        .stamp{border:2px solid #0f7a3d;color:#0f7a3d;border-radius:8px;padding:6px 14px;font-weight:800;transform:rotate(-4deg);font-size:12px}
        @media print{body{padding:16px}}
      </style></head><body>
      <div class="hd">
        <div><h1>Güvenlik Kanıt Raporu</h1><div class="sub">${co} · ${winLabel}</div></div>
        <div style="text-align:right"><div class="badge">${postureLabel}</div><div class="sub" style="margin-top:6px">Oluşturma: ${genAt}</div></div>
      </div>
      <div class="grid">
        <div>
          <h2>Olaylar (${winLabel})</h2>
          <table>
            ${row("Başarısız giriş", e.failed_logins ?? 0)}
            ${row("Başarılı giriş", e.successful_logins ?? 0)}
            ${row("İmkânsız seyahat", e.impossible_travel ?? 0)}
            ${row("Yeni cihaz", e.new_devices ?? 0)}
            ${row("Kimlik doğrulama (step-up)", e.step_ups ?? 0)}
            ${row("Kritik işlem", e.critical_submits ?? 0)}
            ${row("Hesap kilidi olayı", e.account_locked ?? 0)}
          </table>
        </div>
        <div>
          <h2>Duruş & Güvenlik</h2>
          <table>
            ${row("Aktif oturum (anlık)", data.active_sessions ?? 0)}
            ${row("2FA kapsamı", `${data.twofa?.enabled_users ?? 0}/${data.twofa?.total_users ?? 0} kullanıcı`)}
            ${row("Kilitli hesap", data.locked_accounts ?? 0)}
            ${row("İmkânsız seyahat izleme", data.impossible_travel_monitoring ? "Açık" : "Kapalı")}
            ${row("Değişiklik defteri", `${data.ledger?.entries ?? 0} kayıt · ${data.ledger?.intact ? "Bozulmamış" : "UYUŞMAZLIK"}`)}
          </table>
        </div>
      </div>
      <h2>En Çok Başarısız Giriş (IP)</h2>
      <table>${ipRows}</table>
      <div class="sig">
        <div>Raporu oluşturan: <b>${(user && (user.name || user.email)) || "—"}</b><br/>Bütünlük: değişiklik defteri hash-zinciri ${data.ledger?.intact ? "doğrulandı ✓" : "UYUŞMAZLIK ✕"}</div>
        <div class="stamp">GROOT ONE · KANIT</div>
      </div>
      </body></html>`;
    const w = window.open("", "secproof_pdf", "width=900,height=1000");
    if (!w) return;
    w.document.write(html); w.document.close(); w.focus();
    setTimeout(() => { try { w.print(); } catch (err) { /* ignore */ } }, 350);
  };

  const tiles = [
    { key: "failed-logins", label: "Başarısız Giriş", value: ev.failed_logins, Icon: LogIn, tone: ev.failed_logins ? "rose" : "slate" },
    { key: "impossible", label: "İmkânsız Seyahat", value: ev.impossible_travel, Icon: PlaneTakeoff, tone: ev.impossible_travel ? "rose" : "slate" },
    { key: "new-devices", label: "Yeni Cihaz", value: ev.new_devices, Icon: MonitorSmartphone, tone: ev.new_devices ? "amber" : "slate" },
    { key: "stepup", label: "Kimlik Doğrulama", value: ev.step_ups, Icon: KeyRound, tone: "slate" },
    { key: "critical", label: "Kritik İşlem", value: ev.critical_submits, Icon: PackageCheck, tone: "slate" },
    { key: "active", label: "Aktif Oturum", value: data?.active_sessions, Icon: Users, tone: "emerald" },
  ];

  return (
    <div className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-slate-200" data-testid="security-proof-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-3 bg-slate-900 px-5 py-4">
        <div className="flex flex-wrap items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-green/20 text-brand-green"><ShieldCheck className="h-5 w-5" /></span>
          <div>
            <h2 className="font-heading text-lg font-bold text-white">Güvenlik Kanıt Panosu</h2>
            <p className="text-[11px] text-slate-400">Son {win === 24 ? "24 saat" : "7 gün"} · salt-okunur kanıt özeti</p>
          </div>
          <span data-testid="secproof-posture" className={`ml-1 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold ring-1 ring-inset ${posture.cls}`}>
            <posture.Icon className="h-3.5 w-3.5" /> {posture.label}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg bg-white/10 p-0.5">
            {[[24, "24s"], [168, "7g"]].map(([h, l]) => (
              <button key={h} data-testid={`secproof-window-${h}`} onClick={() => setWin(h)}
                className={`rounded-md px-2.5 py-1 text-xs font-semibold transition-colors ${win === h ? "bg-white text-slate-900" : "text-slate-300 hover:text-white"}`}>{l}</button>
            ))}
          </div>
          <button data-testid="secproof-export-pdf" onClick={exportPdf} title="İmzalı PDF olarak dışa aktar" className="inline-flex items-center gap-1.5 rounded-lg bg-white/10 px-2.5 py-1.5 text-xs font-semibold text-slate-200 transition-colors hover:bg-white/20">
            <FileDown className="h-3.5 w-3.5" /> PDF
          </button>
          <button data-testid="secproof-refresh" onClick={load} title="Yenile" className="inline-flex items-center gap-1 rounded-lg bg-white/10 px-2.5 py-1.5 text-xs font-semibold text-slate-200 transition-colors hover:bg-white/20">
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          </button>
        </div>
      </div>

      <div className="space-y-4 p-5">
        <p className="text-xs text-slate-500" data-testid="secproof-note">{posture.note}</p>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {tiles.map((t) => (
            <div key={t.key} data-testid={`secproof-stat-${t.key}`} className={`rounded-xl border-l-4 bg-slate-50 px-3 py-2.5 ${TONE[t.tone]}`}>
              <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide opacity-80"><t.Icon className="h-3.5 w-3.5" /> {t.label}</div>
              <div className="mt-1 font-heading text-2xl font-bold text-slate-900">{loading ? "…" : (t.value ?? 0)}</div>
            </div>
          ))}
        </div>

        <div data-testid="secproof-trend" className="grid gap-3 sm:grid-cols-2">
          <TrendRow label="Başarısız Giriş (7 gün)" testid="secproof-trend-failed" series={trend.map((d) => d.failed_logins)} color="#e11d48" />
          <TrendRow label="İmkânsız Seyahat (7 gün)" testid="secproof-trend-impossible" series={trend.map((d) => d.impossible_travel)} color="#f59e0b" />
        </div>

        <div className="grid gap-3 md:grid-cols-3">
          <div data-testid="secproof-stat-2fa" className="rounded-xl bg-slate-50 p-3 ring-1 ring-inset ring-slate-200">
            <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500"><KeyRound className="h-3.5 w-3.5" /> 2FA Kapsamı</div>
            <div className="mt-1.5 flex items-end gap-2">
              <span className="font-heading text-xl font-bold text-slate-900">{data?.twofa?.enabled_users ?? 0}/{data?.twofa?.total_users ?? 0}</span>
              <span className="text-xs text-slate-500">kullanıcı</span>
            </div>
            <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
              <div className="h-full rounded-full bg-brand-green transition-all" style={{ width: `${twofaPct}%` }} data-testid="secproof-2fa-bar" />
            </div>
            {data?.twofa?.mandatory_roles?.length
              ? <p className="mt-1.5 text-[10px] text-slate-400">Zorunlu roller: {data.twofa.mandatory_roles.join(", ")}</p>
              : <p className="mt-1.5 text-[10px] text-slate-400">Zorunlu 2FA rolü tanımlı değil.</p>}
          </div>

          <div data-testid="secproof-ledger" className="rounded-xl bg-slate-50 p-3 ring-1 ring-inset ring-slate-200">
            <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500"><Fingerprint className="h-3.5 w-3.5" /> Defter Bütünlüğü</div>
            <div className="mt-1.5 flex items-center gap-2">
              {data?.ledger?.intact
                ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-bold text-emerald-700"><ShieldCheck className="h-3.5 w-3.5" /> Bozulmamış</span>
                : <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-xs font-bold text-rose-700"><ShieldAlert className="h-3.5 w-3.5" /> Uyuşmazlık</span>}
            </div>
            <p className="mt-1.5 text-[11px] text-slate-500">{data?.ledger?.entries ?? 0} kayıt hash-zinciriyle doğrulandı</p>
            <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-slate-400">
              <Lock className="h-3 w-3" /> {data?.locked_accounts ?? 0} kilitli hesap · İmkânsız seyahat {data?.impossible_travel_monitoring ? "açık" : "kapalı"}
            </div>
          </div>

          <div data-testid="secproof-ip-list" className="rounded-xl bg-slate-50 p-3 ring-1 ring-inset ring-slate-200">
            <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500"><ShieldAlert className="h-3.5 w-3.5" /> En Çok Başarısız Giriş (IP)</div>
            {(data?.top_failing_ips || []).length === 0
              ? <p className="mt-2 text-xs text-slate-400">Başarısız giriş kaydı yok.</p>
              : (
                <ul className="mt-2 space-y-1">
                  {data.top_failing_ips.map((ip, i) => (
                    <li key={ip.ip} data-testid={`secproof-ip-${i}`} className="flex items-center justify-between gap-2 text-xs">
                      <span className="truncate font-mono text-slate-600">{ip.ip}</span>
                      <span className="shrink-0 rounded-full bg-rose-100 px-2 py-0.5 font-bold text-rose-700">{ip.count}</span>
                    </li>
                  ))}
                </ul>
              )}
          </div>
        </div>
      </div>
    </div>
  );
}
